import { api, getConfig } from './api';
import { navigate } from './router';
import type { BusinessType } from './townProfile';

/**
 * Owner sign-in through Cognito managed login (authorization code + PKCE, public client). Cognito proves who the
 * owner is; the API then exchanges that for a FirstMango token scoped to ONE restaurant (`staffSession`).
 */
interface CognitoTokens { idToken: string; refreshToken?: string; expiresAt: number }
export interface StaffSession { token: string; tenantId: string; name: string; role: string; expiresAt: number; business?: BusinessType }

const K_COG = 'fm.cognito';
const K_STAFF = 'fm.staff';
const K_PKCE = 'fm.pkce';

const read = <T,>(k: string): T | null => { try { return JSON.parse(localStorage.getItem(k) ?? 'null') as T | null; } catch { return null; } };
const write = (k: string, v: unknown) => { try { if (v === null) localStorage.removeItem(k); else localStorage.setItem(k, JSON.stringify(v)); } catch { /* storage blocked */ } };

const b64url = (buf: ArrayBuffer | Uint8Array) =>
  btoa(String.fromCharCode(...new Uint8Array(buf as ArrayBuffer))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
const redirectUri = () => `${location.origin}/auth/callback`;

export async function startSignIn() {
  const { loginUrl, clientId } = await getConfig();
  const verifier = b64url(crypto.getRandomValues(new Uint8Array(48)));
  const state = b64url(crypto.getRandomValues(new Uint8Array(16)));
  const challenge = b64url(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));
  try { sessionStorage.setItem(K_PKCE, JSON.stringify({ verifier, state })); } catch { /* ignore */ }
  const q = new URLSearchParams({
    response_type: 'code', client_id: clientId, redirect_uri: redirectUri(), scope: 'openid email profile',
    code_challenge: challenge, code_challenge_method: 'S256', state,
  });
  location.assign(`${loginUrl}/oauth2/authorize?${q}`);
}

async function tokenRequest(body: Record<string, string>) {
  const { loginUrl, clientId } = await getConfig();
  const res = await fetch(`${loginUrl}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ client_id: clientId, ...body }),
  });
  if (!res.ok) throw new Error('sign-in failed');
  return (await res.json()) as { id_token: string; refresh_token?: string; expires_in: number };
}

/** /auth/callback?code&state */
export async function finishSignIn(): Promise<void> {
  const p = new URLSearchParams(location.search);
  let saved: { verifier: string; state: string } | null = null;
  try { saved = JSON.parse(sessionStorage.getItem(K_PKCE) ?? 'null'); sessionStorage.removeItem(K_PKCE); } catch { /* ignore */ }
  if (!saved || p.get('state') !== saved.state || !p.get('code')) throw new Error('sign-in expired, please try again');
  const t = await tokenRequest({ grant_type: 'authorization_code', code: p.get('code')!, redirect_uri: redirectUri(), code_verifier: saved.verifier });
  write(K_COG, { idToken: t.id_token, refreshToken: t.refresh_token, expiresAt: Date.now() + (t.expires_in - 60) * 1000 } satisfies CognitoTokens);
}

/** A valid Cognito ID token, refreshed if needed; null when signed out. */
export async function idToken(): Promise<string | null> {
  const c = read<CognitoTokens>(K_COG);
  if (!c) return null;
  if (c.expiresAt > Date.now()) return c.idToken;
  if (!c.refreshToken) return null;
  try {
    const t = await tokenRequest({ grant_type: 'refresh_token', refresh_token: c.refreshToken });
    write(K_COG, { idToken: t.id_token, refreshToken: c.refreshToken, expiresAt: Date.now() + (t.expires_in - 60) * 1000 });
    return t.id_token;
  } catch {
    write(K_COG, null);
    return null;
  }
}

export async function signOut() {
  const { loginUrl, clientId } = await getConfig();
  const c = read<CognitoTokens>(K_COG);
  if (c?.refreshToken) {
    // Revoke the long-lived refresh token so a copy taken from this browser stops working.
    await fetch(`${loginUrl}/oauth2/revoke`, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ token: c.refreshToken, client_id: clientId }),
    }).catch(() => undefined);
  }
  write(K_COG, null);
  write(K_STAFF, null);
  location.assign(`${loginUrl}/logout?${new URLSearchParams({ client_id: clientId, logout_uri: `${location.origin}/owner` })}`);
}

export function staffSession(): StaffSession | null {
  const s = read<StaffSession>(K_STAFF);
  return s && s.expiresAt > Date.now() ? s : null;
}

export async function openRestaurant(tenantId: string, name: string) {
  const id = await idToken();
  if (!id) return void navigate('/owner');
  const r = await api<{ token: string; role: string; expiresIn: number; business?: BusinessType }>('POST', '/auth/owner', { token: id, body: { tenantId } });
  write(K_STAFF, { token: r.token, tenantId, name, role: r.role, expiresAt: Date.now() + (r.expiresIn - 60) * 1000, business: r.business ?? 'shop' } satisfies StaffSession);
  navigate('/floor');
}

/** Street stalls, market stalls and food trucks serve at a counter: no floor plan, one stall QR. */
export const isVendor = (s: Pick<StaffSession, 'business'> | null | undefined) => !!s?.business && s.business !== 'shop';

/** The owner changed the business type (town storefront): the staff screens follow without signing in again. */
export function setSessionBusiness(business: BusinessType) {
  const s = staffSession();
  if (s) write(K_STAFF, { ...s, business });
}

export function leaveRestaurant() {
  write(K_STAFF, null);
  navigate('/owner');
}
